/* Scoped storefront search. No catalogue download, app, or private API token. */
(() => {
  const MODES = {
    name: { label: 'Fragrance name', fields: 'title', placeholder: 'Enter a fragrance name', help: 'Find a Julianna’s Perfume fragrance by name.' },
    notes: { label: 'Perfume notes', fields: 'body,tag', placeholder: 'Try vanilla, rose or sandalwood', help: 'Discover fragrances with the notes you love.' },
    original: { label: 'Original name', fields: 'body,tag', placeholder: 'Enter the original perfume name', help: 'Find our interpretation of an original perfume.' },
  };
  const cache = new Map();
  const normalize = (value) => String(value || '').normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim().replace(/\s+/g, ' ');
  const quote = (value) => `"${value.replace(/[\\"():]/g, '\\$&')}"`;
  const stopwords = new Set(['a', 'an', 'and', 'or', 'the', 'in', 'of', 'with', 'by', 'for', 'to']);

  function buildQuery(term, mode) {
    const clean = term.trim().replace(/\s+/g, ' ');
    if (!clean || !MODES[mode]) return '';
    const fields = mode === 'name' ? ['title'] : ['body', 'tag'];
    const clause = (text) => `(${fields.map((field) => `${field}:${quote(text)}`).join(' OR ')})`;
    const phrase = clause(clean);
    const words = clean.split(' ').filter((word) => !stopwords.has(normalize(word)));
    // Keep a whole-phrase branch: independently scoped stopwords otherwise lose names like "In The M'Oud".
    return words.length > 1 ? `${phrase} OR (${words.map(clause).join(' AND ')})` : phrase;
  }

  function rankProducts(products, term, mode) {
    const query = normalize(term);
    const words = query.split(' ').filter(Boolean);
    const score = (product) => {
      const title = normalize(product.title);
      if (mode === 'name') {
        if (title === query) return 10000;
        if (title.startsWith(`${query} `)) return 8000;
        if (title.startsWith(query)) return 7000;
        if (title.includes(query)) return 6000;
        return words.filter((word) => title.split(' ').some((part) => part.startsWith(word))).length * 100;
      }
      const tags = product.tags.map(normalize);
      const body = normalize(product.body);
      const exactTag = tags.includes(query);
      const phrase = body.includes(query) || tags.some((tag) => tag.includes(query));
      const coverage = words.filter((word) => tags.some((tag) => tag.includes(word)) || body.includes(word)).length;
      return (exactTag ? (mode === 'notes' ? 5000 : 4000) : 0) + (phrase ? (mode === 'original' ? 5000 : 2000) : 0) + coverage * 100;
    };
    return products.map((product, index) => ({ product, index, score: score(product) }))
      .sort((a, b) => b.score - a.score || a.index - b.index).map(({ product }) => product);
  }

  function searchContext() {
    const params = new URLSearchParams(location.search);
    const mode = params.get('jp_mode');
    const term = params.get('jp_q');
    return MODES[mode] && term && params.get('q') === buildQuery(term, mode) ? { mode, term, params } : null;
  }

  function preserveSearchParams(search) {
    const context = searchContext();
    if (!context || !document.querySelector('.template-search')) return search;
    const params = new URLSearchParams(search);
    // Facet and clear-all links can drop the custom context; retain the same scoped query.
    for (const key of ['q', 'jp_q', 'jp_mode', 'type', 'options[prefix]', 'options[unavailable_products]']) {
      if (context.params.has(key)) params.set(key, context.params.get(key));
    }
    return params.toString();
  }

  function updateResultsPage() {
    const context = searchContext();
    if (!context || !document.querySelector('.template-search')) return;
    const shopName = document.querySelector('jp-fragrance-search')?.dataset.shopName || 'Julianna’s Perfume';
    document.title = `${context.term} – ${MODES[context.mode].label} – ${shopName}`;
    document.querySelectorAll('[data-jp-search-term]').forEach((element) => { element.textContent = context.term; });
    document.querySelectorAll('[data-jp-search-mode-label]').forEach((element) => { element.textContent = MODES[context.mode].label; });
    document.querySelectorAll('[data-jp-search-context]').forEach((element) => { element.hidden = false; });
    document.querySelectorAll('.template-search a[href]').forEach((link) => {
      const url = new URL(link.href, location.origin);
      if (url.pathname !== location.pathname || !url.searchParams.has('q')) return;
      url.search = preserveSearchParams(url.search);
      link.href = url.href;
    });
    if (context.mode !== 'name' || (context.params.get('sort_by') && context.params.get('sort_by') !== 'relevance')) return;
    const grid = document.querySelector('.template-search .product-grid');
    if (!grid) return;
    const rows = Array.from(grid.children).map((element) => ({ element, title: element.dataset.jpResultTitle }));
    rankProducts(rows, context.term, 'name').forEach(({ element }) => grid.append(element));
  }

  class JPFragranceSearch extends HTMLElement {
    get isOpen() { return Boolean(this.results && !this.results.hidden); }

    connectedCallback() {
      if (this.listeners) return;
      this.form = this.querySelector('form');
      this.input = this.querySelector('[data-search-input]');
      if (!this.form || !this.input) return;
      this.submit = this.querySelector('[data-search-submit]');
      this.clear = this.querySelector('[data-search-clear]');
      this.help = this.querySelector('[data-search-help]');
      this.results = this.querySelector('[data-search-results]');
      this.status = this.querySelector('[data-search-status]');
      this.radios = Array.from(this.querySelectorAll('input[name="jp_mode"]'));
      this.listeners = new AbortController();
      const listen = (target, event, handler, capture = false, passive = false) => target?.addEventListener(event, handler, { signal: this.listeners.signal, capture, passive });
      listen(this.form, 'change', (event) => { if (event.target.name === 'jp_mode') this.setMode(event.target.value); });
      listen(this.input, 'input', () => this.onInput());
      listen(this.input, 'focus', () => { if (this.input.value.trim() && this.mode && !this.isOpen) this.scheduleSearch(); });
      listen(this.form, 'reset', (event) => {
        event.preventDefault();
        this.input.value = '';
        this.onInput();
        this.input.focus();
      });
      listen(this.form, 'submit', (event) => this.onSubmit(event));
      listen(this.input, 'keydown', (event) => this.onKeydown(event));
      // A touchscreen tap can end without a compatibility click after keyboard
      // dismissal. Handle a completed tap directly, keeping scrolling native.
      listen(document, 'pointerdown', (event) => this.onProductPointerDown(event), true);
      listen(document, 'pointermove', (event) => this.onProductPointerMove(event), true, true);
      listen(document, 'pointerup', (event) => this.onProductPointerUp(event), true);
      listen(document, 'pointercancel', (event) => this.onProductPointerCancel(event), true);
      listen(document, 'scroll', () => {
        if (this.productPointer) this.productPointer.cancelled = true;
      }, true, true);
      listen(this.results, 'contextmenu', () => {
        if (this.productPointer) this.productPointer.cancelled = true;
      }, true);
      // Older mobile WebViews expose Touch Events without Pointer Events.
      if (!window.PointerEvent) {
        const pointerFromTouch = (event, touch) => ({
          target: event.target, pointerId: touch.identifier, pointerType: 'touch', isPrimary: true,
          button: 0, clientX: touch.clientX, clientY: touch.clientY,
          metaKey: event.metaKey, ctrlKey: event.ctrlKey, shiftKey: event.shiftKey, altKey: event.altKey,
          cancelable: event.cancelable, preventDefault: () => event.preventDefault(),
        });
        listen(document, 'touchstart', (event) => {
          if (event.touches.length !== 1) {
            if (this.productPointer) this.productPointer.cancelled = true;
            return;
          }
          this.onProductPointerDown(pointerFromTouch(event, event.touches[0]));
        }, true, true);
        for (const [type, handler] of [['touchmove', 'onProductPointerMove'], ['touchend', 'onProductPointerUp'], ['touchcancel', 'onProductPointerCancel']]) {
          listen(document, type, (event) => {
            const touch = Array.from(event.changedTouches).find((item) => item.identifier === this.productPointer?.pointerId);
            if (touch) this[handler](pointerFromTouch(event, touch));
          }, true, type === 'touchmove');
        }
      }
      listen(this.results, 'click', (event) => this.onProductClick(event), true);
      // Safari may blur the input without focusing the tapped link.
      listen(this, 'focusout', (event) => {
        if (!this.productPointer && event.relatedTarget && !this.contains(event.relatedTarget)) this.cancel();
      });
      this.details = this.closest('details');
      this.modal = this.closest('.jp-search-modal');
      listen(this.details, 'toggle', () => {
        if (!this.details.open) this.cancel();
        this.updateViewport();
      });
      listen(window.visualViewport, 'resize', () => this.updateViewport());
      listen(window.visualViewport, 'scroll', () => this.updateViewport());
      listen(window, 'resize', () => this.updateViewport());
      this.updateViewport();
      const context = searchContext();
      if (context) this.input.value = context.term;
      else this.input.value = '';
      this.setMode(context?.mode || '', false);
    }

    disconnectedCallback() {
      this.cancel();
      this.listeners?.abort();
      this.listeners = null;
    }

    updateViewport() {
      if (!this.modal) return;
      const viewport = window.visualViewport;
      const height = viewport?.height || window.innerHeight;
      this.modal.style.setProperty('--jp-search-viewport-height', `${height}px`);
      this.modal.style.setProperty('--jp-search-viewport-width', `${viewport?.width || window.innerWidth}px`);
      this.modal.style.setProperty('--jp-search-viewport-top', `${Math.max(0, viewport?.offsetTop || 0)}px`);
      this.modal.style.setProperty('--jp-search-viewport-left', `${Math.max(0, viewport?.offsetLeft || 0)}px`);
      // Compact controls when the keyboard (or landscape) leaves little room.
      // Keep focus in the field; do not force a blur or scroll the result list.
      this.modal.dataset.searchCompact = height < 600 ? 'true' : 'false';
    }

    setMode(mode, search = true) {
      this.cancel();
      this.mode = MODES[mode] ? mode : '';
      this.radios.forEach((radio) => { radio.checked = radio.value === this.mode; });
      this.input.disabled = !this.mode;
      this.input.placeholder = MODES[this.mode]?.placeholder || 'Choose a search type first';
      this.help.textContent = MODES[this.mode]?.help || 'Choose one search type to begin.';
      this.dataset.mode = this.mode;
      this.updateControls();
      if (search && this.mode && this.input.value.trim()) this.scheduleSearch();
    }

    updateControls() {
      this.submit.disabled = !this.mode || !this.input.value.trim();
      this.clear.hidden = !this.input.value.length;
    }

    stopPendingSearch() {
      clearTimeout(this.timer);
      this.request?.abort();
      this.request = null;
    }

    cancel() {
      this.stopPendingSearch();
      clearTimeout(this.pointerReleaseTimer);
      this.productPointer = null;
      this.closeResults();
    }

    productLink(target) {
      const link = target?.closest?.('a.jp-search__result[href]');
      return link && this.results?.contains(link) ? link : null;
    }

    canOpenProduct(link, event) {
      return link && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey &&
        !link.hasAttribute('download') && (!link.target || link.target === '_self');
    }

    onProductPointerDown(event) {
      clearTimeout(this.pointerReleaseTimer);
      if (event.isPrimary === false) {
        if (this.productPointer) this.productPointer.cancelled = true;
        return;
      }
      this.suppressTouchClick = null;
      const link = this.productLink(event.target);
      this.productPointer = link ? {
        link, pointerId: event.pointerId, pointerType: event.pointerType,
        x: event.clientX, y: event.clientY, started: Date.now(), cancelled: event.button !== 0,
      } : null;
      if (link) this.stopPendingSearch();
      else if (!this.contains(event.target)) this.cancel();
    }

    onProductPointerMove(event) {
      const press = this.productPointer;
      if (!press || press.pointerId !== event.pointerId) return;
      // Once a gesture becomes a drag, returning to its start is still a drag.
      if (Math.hypot(event.clientX - press.x, event.clientY - press.y) > 10) press.cancelled = true;
    }

    onProductPointerUp(event) {
      const press = this.productPointer;
      if (!press || press.pointerId !== event.pointerId) return;
      if (!['touch', 'pen'].includes(press.pointerType)) {
        this.pointerReleaseTimer = setTimeout(() => { this.productPointer = null; }, 0);
        return;
      }
      this.onProductPointerMove(event);
      this.productPointer = null;
      if (!this.canOpenProduct(press.link, event)) return;
      this.suppressTouchClick = { link: press.link, until: Date.now() + 750 };
      if (press.cancelled || !press.link.isConnected || Date.now() - press.started > 500) return;
      if (event.cancelable) event.preventDefault();
      this.stopPendingSearch();
      this.openProduct(press.link);
    }

    onProductPointerCancel(event) {
      const press = this.productPointer;
      if (!press || press.pointerId !== event.pointerId) return;
      this.suppressTouchClick = { link: press.link, until: Date.now() + 750 };
      this.productPointer = null;
    }

    onProductClick(event) {
      const link = this.productLink(event.target);
      if (event.button !== 0 || !this.canOpenProduct(link, event)) return;
      // A touch browser may still emit a delayed click after the tap was handled.
      // Keyboard activation has detail 0 and must remain available immediately.
      if (event.detail > 0 && this.suppressTouchClick?.link === link && Date.now() <= this.suppressTouchClick.until) {
        event.preventDefault();
        return;
      }
      event.preventDefault();
      this.stopPendingSearch();
      clearTimeout(this.pointerReleaseTimer);
      this.productPointer = null;
      this.openProduct(link);
    }

    openProduct(link) {
      // Explicit navigation also works when another theme/app click listener
      // prevents an anchor's default action. Keep Shopify's full product URL.
      location.assign(link.href);
    }

    closeResults() {
      if (!this.results) return;
      this.results.hidden = true;
      this.results.replaceChildren();
      this.results.removeAttribute('aria-busy');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.input.setAttribute('aria-controls', this.results.id);
      this.activeIndex = -1;
      this.options = [];
    }

    onInput() {
      this.cancel(); // Invalidate immediately, before the debounce delay.
      this.updateControls();
      if (this.mode && this.input.value.trim()) this.scheduleSearch();
      else this.status.textContent = '';
    }

    scheduleSearch() {
      clearTimeout(this.timer);
      if (this.dataset.predictiveEnabled !== 'true') return;
      this.timer = setTimeout(() => this.search(), 250);
    }

    async fetchProducts(url, signal) {
      const response = await fetch(url, { signal, headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error(`Search returned ${response.status}`);
      const document = new DOMParser().parseFromString(await response.text(), 'text/html');
      const wrapper = document.querySelector('[data-jp-search-response]');
      if (!wrapper) throw new Error('Search response missing');
      const products = Array.from(wrapper.querySelectorAll('template[data-jp-search-product]')).map((template) => ({
        id: template.dataset.productId, title: template.dataset.title || '',
        tags: JSON.parse(template.dataset.tags || '[]'), body: template.dataset.body || '', template,
      }));
      return { products, next: wrapper.dataset.nextUrl };
    }

    async exactProducts(term, signal) {
      let url = new URL(this.form.action, location.origin);
      url.search = new URLSearchParams({ q: `title:${quote(term)}`, type: 'product', 'options[prefix]': 'none', 'options[unavailable_products]': 'show', section_id: 'jp-search-suggestions' });
      const products = [];
      // Only search-index-visible products; follow a narrow phrase query if it spans multiple pages.
      while (url) {
        const page = await this.fetchProducts(url, signal);
        products.push(...page.products);
        if (products.some((product) => normalize(product.title) === normalize(term)) || !page.next) break;
        url = new URL(page.next, location.origin);
        url.searchParams.set('section_id', 'jp-search-suggestions');
      }
      return products;
    }

    async search() {
      const term = this.input.value.trim();
      const mode = this.mode;
      if (!term || !mode || (this.details && !this.details.open)) return;
      this.request?.abort();
      const request = new AbortController();
      this.request = request;
      const current = () => this.isConnected && this.request === request && !request.signal.aborted && this.mode === mode && this.input.value.trim() === term;
      const key = JSON.stringify([this.dataset.predictiveUrl, mode, term.toLocaleLowerCase()]);
      if (cache.has(key)) { this.render(cache.get(key), term); return; }
      this.status.textContent = 'Searching fragrances…';
      this.results.hidden = false;
      this.results.setAttribute('aria-busy', 'true');
      this.message('Searching fragrances…');
      try {
        const url = new URL(this.dataset.predictiveUrl, location.origin);
        url.search = new URLSearchParams({ q: term, 'resources[type]': 'product', 'resources[limit]': '10', 'resources[options][fields]': MODES[mode].fields, 'resources[options][unavailable_products]': 'show', section_id: 'jp-search-suggestions' });
        const requests = [this.fetchProducts(url, request.signal).then((page) => page.products)];
        if (mode === 'name') requests.push(this.exactProducts(term, request.signal));
        const responses = await Promise.allSettled(requests);
        if (!current()) return;
        if (responses.every((response) => response.status === 'rejected')) throw new Error('Search unavailable');
        const unique = new Map();
        responses.forEach((response) => { if (response.status === 'fulfilled') response.value.forEach((product) => unique.set(product.id, product)); });
        const products = rankProducts(Array.from(unique.values()), term, mode).slice(0, 8);
        if (responses.every((response) => response.status === 'fulfilled')) {
          if (cache.size >= 50) cache.delete(cache.keys().next().value);
          cache.set(key, products);
        }
        this.render(products, term);
      } catch (error) {
        if (!current() || error.name === 'AbortError') return;
        this.closeResults();
        this.results.hidden = false;
        this.message('Suggestions are temporarily unavailable. Press Search to see results.');
        this.status.textContent = 'Suggestions are temporarily unavailable. You can still search.';
      } finally {
        if (current()) this.results.removeAttribute('aria-busy');
      }
    }

    message(text) {
      const paragraph = document.createElement('p');
      paragraph.className = 'jp-search__message';
      paragraph.textContent = text;
      this.results.replaceChildren(paragraph);
    }

    render(products, term) {
      this.closeResults();
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      const heading = document.createElement('p');
      heading.className = 'jp-search__results-heading';
      heading.textContent = `${MODES[this.mode].label} · Top matches`;
      this.results.append(heading);
      if (!products.length) {
        this.message('No suggestions yet. Try another name or note, or view all results.');
      } else {
        const list = document.createElement('ul');
        list.className = 'jp-search__result-list';
        list.setAttribute('role', 'listbox');
        list.id = `${this.input.id}-Options`;
        list.setAttribute('aria-label', 'Fragrance suggestions');
        this.input.setAttribute('aria-controls', list.id);
        products.forEach((product, index) => {
          const item = document.createElement('li');
          item.id = `${this.input.id}-Option-${index}`;
          item.setAttribute('role', 'option');
          item.setAttribute('aria-selected', 'false');
          item.append(document.importNode(product.template.content, true));
          const badge = item.querySelector('[data-search-match]');
          if (badge) badge.hidden = this.mode !== 'name' || normalize(product.title) !== normalize(term);
          list.append(item);
        });
        this.results.append(list);
        this.options = Array.from(list.children);
      }
      const all = document.createElement('button');
      all.type = 'submit';
      all.className = 'jp-search__view-all';
      all.textContent = `View all results for “${term}”`;
      this.results.append(all);
      this.status.textContent = products.length ? `${products.length} suggestions for ${term}, by ${MODES[this.mode].label.toLowerCase()}.` : `No suggestions for ${term}.`;
    }

    onKeydown(event) {
      if (event.key === 'Escape' && !this.results.hidden) {
        event.preventDefault();
        event.stopPropagation();
        this.cancel();
        return;
      }
      if (event.key === 'Enter' && this.activeIndex >= 0 && !this.results.hidden) {
        event.preventDefault();
        this.options[this.activeIndex]?.querySelector('a')?.click();
        return;
      }
      if (!['ArrowDown', 'ArrowUp'].includes(event.key) || !this.options?.length || this.results.hidden) return;
      event.preventDefault();
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      this.activeIndex = this.activeIndex < 0 ? (direction === 1 ? 0 : this.options.length - 1) : (this.activeIndex + direction + this.options.length) % this.options.length;
      this.options.forEach((option, index) => option.setAttribute('aria-selected', String(index === this.activeIndex)));
      const active = this.options[this.activeIndex];
      this.input.setAttribute('aria-activedescendant', active.id);
      active.scrollIntoView({ block: 'nearest' });
    }

    onSubmit(event) {
      event.preventDefault();
      const term = this.input.value.trim();
      if (!this.mode) { this.radios[0]?.focus(); return; }
      if (!term) { this.input.focus(); return; }
      const url = new URL(this.form.action, location.origin);
      url.search = new URLSearchParams(new FormData(this.form));
      url.searchParams.set('q', buildQuery(term, this.mode));
      url.searchParams.set('jp_q', term);
      url.searchParams.set('jp_mode', this.mode);
      this.cancel();
      location.assign(url.href);
    }
  }

  window.JPFragranceSearch = { normalize, buildQuery, preserveSearchParams, rankProducts };
  if (!customElements.get('jp-fragrance-search')) customElements.define('jp-fragrance-search', JPFragranceSearch);
  document.addEventListener('jp:collection-updated', updateResultsPage);
  window.addEventListener('popstate', () => {
    const context = searchContext();
    document.querySelectorAll('jp-fragrance-search').forEach((search) => {
      if (!search.input) return;
      search.input.value = context?.term || '';
      search.setMode(context?.mode || '', false);
    });
    updateResultsPage();
  });
  updateResultsPage();
})();
